import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Stethoscope } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * ProtectedRoute — guards routes with authentication and authorization.
 *
 * Authorization supports TWO modes (can be combined):
 *  1. requiredRoles — legacy static whitelist (e.g. ['admin', 'pharmacist'])
 *  2. requiredPermission — dynamic DB-driven check: { module: 'pharmacy', action: 'view' }
 *
 * When BOTH are provided, access is granted if EITHER check passes.
 * This enables gradual migration from hardcoded roles to dynamic permissions.
 */
const ProtectedRoute = ({
  children,
  requiredRoles = [],
  requiredPermission = null,   // { module: string, action?: string }
  fallbackComponent = null
}) => {
  const { user, userProfile, loading, profileLoading, hasPermission } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [accessDenied, setAccessDenied] = useState(false);
  const [redirectTarget, setRedirectTarget] = useState(null);

  useEffect(() => {
    // Only redirect if user is definitely not authenticated and loading is complete
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Handle access denied redirect in useEffect to avoid setState during render
  useEffect(() => {
    if (accessDenied && redirectTarget) {
      toast.error(`Access Denied: Your role cannot access this section.`);
      const timer = setTimeout(() => {
        navigate(redirectTarget, { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [accessDenied, redirectTarget, navigate, toast]);

  // Show loading state while authentication is being checked
  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <Stethoscope className="h-8 w-8 text-blue-600 animate-pulse" />
            </div>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // User is not authenticated - show fallback or access restricted message
  if (!user) {
    if (fallbackComponent) {
      return fallbackComponent;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full text-center p-8">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-red-100 rounded-full">
              <Stethoscope className="h-12 w-12 text-red-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Restricted</h2>
          <p className="text-gray-600 mb-6">
            Redirecting to login...
          </p>
        </div>
      </div>
    );
  }

  // User is authenticated — check authorization
  const userRole = userProfile?.role;
  const hasRoleRequirement = requiredRoles?.length > 0;
  const hasPermissionRequirement = requiredPermission?.module;

  // If there's any authorization requirement, wait for profile
  if ((hasRoleRequirement || hasPermissionRequirement) && !userProfile) {
    console.log('ProtectedRoute - Waiting for profile...');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying access permissions...</p>
        </div>
      </div>
    );
  }

  // Evaluate access: allow if EITHER check passes (supports gradual migration)
  let hasAccess = true;

  if (hasRoleRequirement || hasPermissionRequirement) {
    const passesRoleCheck = hasRoleRequirement ? requiredRoles.includes(userRole) : false;
    const passesPermissionCheck = hasPermissionRequirement
      ? hasPermission(requiredPermission.module, requiredPermission.action || 'view')
      : false;

    // If both modes are active, pass if EITHER succeeds
    // If only one mode is active, that mode decides
    if (hasRoleRequirement && hasPermissionRequirement) {
      hasAccess = passesRoleCheck || passesPermissionCheck;
    } else if (hasRoleRequirement) {
      hasAccess = passesRoleCheck;
    } else {
      hasAccess = passesPermissionCheck;
    }

    console.log('ProtectedRoute - Access evaluation:', {
      userRole,
      requiredRoles,
      requiredPermission,
      passesRoleCheck,
      passesPermissionCheck,
      hasAccess
    });
  }

  if (!hasAccess) {
    // Set state to trigger redirect in useEffect (avoid setState during render)
    if (!accessDenied) {
      // Role-based dashboard mapping
      const roleDashboards = {
        admin: '/pulse-dashboard',
        doctor: '/doctor-dashboard',
        nurse: '/nursing-dashboard',
        matron: '/nursing-dashboard',
        lab_technician: '/laboratory-dashboard',
        pharmacist: '/pharmacy-dashboard',
        pharmacist_senior: '/pharmacy-dashboard',
        pharmacy_manager: '/pharmacy-dashboard',
        receptionist: '/patient-registration',
        billing_clerk: '/accounts-dashboard',
        accountant: '/accounts-dashboard',
        finance_manager: '/accounts-dashboard',
        auditor: '/accounts-dashboard',
        admin_officer: '/duty-roster',
        user: '/profile-setup'
      };
      
      const targetDashboard = roleDashboards[userRole] || '/profile-setup';
      
      // Set states - useEffect will handle toast and navigation
      setRedirectTarget(targetDashboard);
      setAccessDenied(true);
    }
    
    // Show brief loading state during redirect
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  // User is authenticated and authorized
  return children;
};

export default ProtectedRoute;